/**
 * Empresa fiscal del módulo Fiscal (multi-empresa).
 *
 * Con multi-empresa activo cada empresa del negocio emite e-CF con su propio
 * RNC, certificado y secuencias. El selector de la barra lateral del módulo
 * Fiscal elige con cuál se trabaja; todas las pantallas fiscales mandan
 * `empresa` en sus llamadas y el backend (ecfempresa.py) resuelve emisor,
 * certificado, token y secuencias con ella.
 *
 * Fuera del módulo Fiscal (o con el modo apagado) siempre es la empresa 1 y
 * el emisor es el de siempre (contexto del login): el resto de la app no
 * cambia.
 *
 * La elección se recuerda por usuario en el navegador (las tablets se
 * comparten entre usuarios).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMaterialUIController } from "context";
import { apiPost } from "lib/api";

const EmpresaFiscalContext = createContext(null);

const claveStorage = (usuario) => `empresaFiscal:${usuario || ""}`;

function leerGuardada(usuario) {
  try {
    const v = Number(window.localStorage.getItem(claveStorage(usuario)));
    return v >= 1 ? v : 1;
  } catch (_e) {
    return 1;
  }
}

function guardar(usuario, empresa) {
  try {
    window.localStorage.setItem(claveStorage(usuario), String(empresa));
  } catch (_e) {
    // localStorage no disponible: la elección dura lo que la sesión.
  }
}

export function EmpresaFiscalProvider({ enFiscal, children }) {
  const [controller] = useMaterialUIController();
  const { usuarioLog, claveLog } = controller;
  const [estado, setEstado] = useState({ activo: false, empresas: [] });
  const [elegida, setElegida] = useState(() => leerGuardada(usuarioLog));

  useEffect(() => {
    setElegida(leerGuardada(usuarioLog));
    if (!usuarioLog || !claveLog) {
      setEstado({ activo: false, empresas: [] });
      return undefined;
    }
    let vivo = true;
    apiPost("/api/multiempresa_estado", { usuario: usuarioLog, password: claveLog })
      .then(({ ok, data }) => {
        if (vivo && ok) setEstado({ activo: !!data.activo, empresas: data.empresas || [] });
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [usuarioLog, claveLog]);

  const setEmpresa = useCallback(
    (empresa) => {
      const n = Number(empresa) >= 1 ? Number(empresa) : 1;
      guardar(usuarioLog, n);
      setElegida(n);
    },
    [usuarioLog]
  );

  const valor = useMemo(() => {
    const activo = !!enFiscal && estado.activo && estado.empresas.length > 1;
    // Una empresa guardada que ya no existe (o el modo apagado) vuelve a la 1.
    const existe = estado.empresas.some((e) => Number(e.empresa) === elegida);
    const empresa = activo && existe ? elegida : 1;
    const datos = activo ? estado.empresas.find((e) => Number(e.empresa) === empresa) || null : null;
    return { activo, empresas: estado.empresas, empresa, datos, setEmpresa };
  }, [enFiscal, estado, elegida, setEmpresa]);

  return <EmpresaFiscalContext.Provider value={valor}>{children}</EmpresaFiscalContext.Provider>;
}

/** Remonta su contenido al cambiar de empresa fiscal (solo con el modo activo). */
export function ClaveEmpresaFiscal({ children }) {
  const { activo, empresa } = useEmpresaFiscal();
  return <React.Fragment key={activo ? `empresa-${empresa}` : "empresa-1"}>{children}</React.Fragment>;
}

const SIN_PROVIDER = { activo: false, empresas: [], empresa: 1, datos: null, setEmpresa: () => {} };

/** { activo, empresas, empresa, datos, setEmpresa } de la empresa fiscal. */
export function useEmpresaFiscal() {
  return useContext(EmpresaFiscalContext) || SIN_PROVIDER;
}

/**
 * Emisor de los comprobantes: el membrete de la empresa elegida con
 * multi-empresa activo; si no, el del login (nombreNegocio / rnc_empresa /
 * direccion_empresa), como siempre.
 */
export function useEmisorFiscal() {
  const [controller] = useMaterialUIController();
  const { datos, empresa } = useEmpresaFiscal();
  return useMemo(() => {
    if (datos) {
      return {
        empresa,
        nombre: datos.nombre || "",
        rnc: datos.rnc || "",
        direccion: datos.direccion || "",
        telefono: datos.telefono || "",
      };
    }
    return {
      empresa: 1,
      nombre: controller.nombreNegocio || "",
      rnc: controller.rnc_empresa || "",
      direccion: controller.direccion_empresa || "",
      telefono: "",
    };
  }, [datos, empresa, controller.nombreNegocio, controller.rnc_empresa, controller.direccion_empresa]);
}
